import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Info, Lock, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useEntitlements } from '@/stores/entitlements';
import { manuaisVisiveis, manualHref } from '@/lib/manuals';

function fmtDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('pt-BR');
}

/** Índice dos manuais. A lista vem de `@/lib/manuals`; a tela não conhece manual nenhum. */
export function ManualsPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const [busca, setBusca] = useState('');

  const manuais = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const lista = manuaisVisiveis(papel);
    if (!q) return lista;
    return lista.filter((m) => `${m.titulo} ${m.resumo} ${m.area}`.toLowerCase().includes(q));
  }, [papel, busca]);

  const porArea = useMemo(() => {
    const mapa = new Map<string, typeof manuais>();
    for (const m of manuais) mapa.set(m.area, [...(mapa.get(m.area) ?? []), m]);
    return Array.from(mapa.entries());
  }, [manuais]);

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Manuais</h1>
          <p className="text-sm text-muted-foreground">
            Como usar cada parte da plataforma, em linguagem de operação. Para a lista de tudo que existe,
            veja Info / Updates.
          </p>
        </div>
        <Link
          to="/admin/info"
          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-input px-4 text-sm font-medium hover:bg-accent"
        >
          <Info className="h-4 w-4" />
          Info / Updates
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar manual"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {manuais.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <BookOpen className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {busca ? 'Nenhum manual corresponde à busca.' : 'Nenhum manual disponível para o seu perfil ainda.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        porArea.map(([area, lista]) => (
          <section key={area} className="space-y-2">
            <h2 className="text-sm font-bold text-muted-foreground">{area.toUpperCase()}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {lista.map((m) => (
                <Link key={m.slug} to={manualHref(m)} className="block">
                  <Card className="h-full transition-colors hover:bg-accent/40">
                    <CardContent className="flex h-full flex-col gap-2 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold">{m.titulo}</p>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                      <p className="flex-1 text-xs text-muted-foreground">{m.resumo}</p>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>Atualizado em {fmtDate(m.atualizado)}</span>
                        {m.papeis && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-semibold">
                            <Lock className="h-3 w-3" />
                            {m.papeis.join(', ')}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
